<template>
  <div>
    <div v-if="canEdit" class="mb-2 d-block d-md-flex justify-content-end">
      <b-input
        v-model="membersSearch"
        placeholder="Search members..."
        type="search"
        size="sm"
      />
      <b-btn
        v-b-modal.add-members
        class="ml-2 shrink-0"
        size="sm"
        variant="primary"
      >
        <i class="icon-plus" /> Add Members
      </b-btn>
    </div>

    <div class="overflow-auto text-nowrap">
      <b-table
        :fields="tableFields"
        :items="org.members"
        :per-page="10"
        :current-page="membersPage"
        :filter="membersSearch"
        @filtered="membersTotal = $event.length"
      >
        <template #cell(name)="{ item }">
          {{ item.name || "(no account)" }}
        </template>

        <template #cell(email)="{ item }">
          {{ item.email || item.invite_email }}
        </template>

        <template #cell(accepted)="{ item }">
          <b-badge :variant="item.accepted ? 'success' : 'secondary'">
            {{ item.accepted ? "accepted" : "pending" }}
          </b-badge>
          <b-btn
            v-if="canEdit && !item.accepted && !item.inviteSent"
            v-b-tooltip.hover="'Resend Invite'"
            class="py-0"
            variant="link"
            @click="resendInvite(item)"
          >
            <i class="icon-reload" />
          </b-btn>
        </template>

        <template #cell(tfa_enabled)="{ item }">
          <b-badge v-if="item.tfa_enabled" variant="secondary">
            <i class="fa fa-lock"></i> 2FA
          </b-badge>
          <div v-else>-</div>
        </template>

        <template #cell(role)="{ item }">
          <b-badge v-if="!item.roles" class="mr-1">-</b-badge>
          <template v-else>
            <b-badge
              v-for="role in item.roles.split(',')"
              :key="role"
              :variant="role === 'Owner' ? 'primary' : ''"
              class="mr-1"
            >
              {{ role | capitalize }}
            </b-badge>
          </template>
          <b-btn
            v-if="canEdit && item.accepted"
            v-b-tooltip.hover="'Edit roles'"
            class="py-0 px-2"
            variant="link"
            @click="openEditRolesModal(item.member_id, item.roles)"
          >
            <i class="icon-pencil" />
          </b-btn>
        </template>

        <template #cell(actions)="{ item }">
          <b-btn
            v-if="item.accepted"
            v-b-tooltip.hover="'Reset password'"
            class="py-0 px-2"
            variant="link"
            @click="resetPassword(item)"
          >
            <i class="icon-key" />
          </b-btn>
          <b-btn
            v-if="!item.accepted && !item.inviteSent"
            v-b-tooltip.hover="'Resend invite'"
            class="py-0 px-2"
            variant="link"
            @click="resendInvite(item)"
          >
            <i class="icon-reload" />
          </b-btn>
          <b-btn
            v-if="item.role !== 'owner' && item.id !== user.id"
            v-b-tooltip.hover="'Remove member'"
            class="py-0 px-2"
            variant="link"
            @click="removeMember(item.member_id)"
          >
            <i class="icon-trash text-danger" />
          </b-btn>
        </template>
      </b-table>
    </div>
    <b-pagination
      v-if="membersTotal > 10"
      v-model="membersPage"
      :total-rows="membersTotal"
      :per-page="10"
      align="center"
      class="mb-4"
      prev-text="Prev"
      next-text="Next"
      hide-goto-end-buttons
    />

    <OrgAddMembersModal :org="org" @updated="$emit('updated')" />
    <OrgEditMemberRolesModal
      :org="org"
      :is-owner="isOwner"
      :member-id="selectedMemberId"
      :member-roles="selectedMemberRoles"
      modal-id="edit-member-roles"
      @close="clearSelectedMember"
      @updated="$emit('updated')"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import API from "@/api";

import OrgAddMembersModal from "./OrgAddMembersModal.vue";
import OrgEditMemberRolesModal from "./OrgEditMemberRolesModal.vue";

export default {
  components: {
    OrgAddMembersModal,
    OrgEditMemberRolesModal
  },

  props: {
    org: Object,
    canEdit: Boolean,
    isOwner: Boolean
  },

  data: () => ({
    membersSearch: "",
    membersPage: 1,
    membersTotal: 0,
    selectedMemberId: null,
    selectedMemberRoles: ""
  }),

  computed: {
    ...mapState(["user"]),

    tableFields() {
      const fields = [
        { key: "name", label: "Username", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "accepted", label: "Status", sortable: true },
        { key: "tfa_enabled", label: "2FA", sortable: true },
        { key: "role", label: "Role", sortable: true },
        { key: "actions", label: "Actions", class: "text-center" }
      ];

      if (!this.canEdit) {
        fields.splice(5, 1); // actions
        fields.splice(3, 1); // 2FA
      }

      return fields;
    }
  },

  watch: {
    org: {
      handler() {
        this.membersTotal = this.org.members.length;
      },
      immediate: true
    }
  },

  methods: {
    clearSelectedMember() {
      this.selectedMemberId = null;
      this.selectedMemberRoles = "";
    },

    openEditRolesModal(memberId, memberRoles) {
      this.selectedMemberId = memberId;
      this.selectedMemberRoles = memberRoles;
      this.$bvModal.show("edit-member-roles");
    },

    async removeMember(member_id) {
      const confirm = await this.$bvModal.msgBoxConfirm(
        "Are you sure you want to remove this member from the organization?",
        {
          title: "Remove Member",
          okTitle: "Remove",
          okVariant: "danger",
          size: "sm",
          centered: true
        }
      );
      if (!confirm) return;

      const { success } = await API.removeOrgMember(this.org.id, member_id);
      if (!success) return;

      this.$emit("updated");
      this.$notify({
        type: "success",
        text: "Member removed"
      });
    },

    async resendInvite(item) {
      const { success } = await API.resendOrgInvite(
        this.org.id,
        item.member_id
      );
      if (!success) return;

      this.$set(item, "inviteSent", true);
      this.$notify({
        type: "success",
        text: "Invitation resent"
      });
    },

    async resetPassword(item) {
      const confirm = await this.$bvModal.msgBoxConfirm(
        "Resetting a member's password will send them an email with a link to reset their password. Continue?",
        {
          title: "Reset Password",
          okTitle: "Reset",
          okVariant: "danger",
          size: "sm",
          centered: true
        }
      );
      if (!confirm) return;

      const { success } = await API.resetOrgMemberPassword(
        this.org.id,
        item.member_id
      );
      if (!success) return;

      this.$notify({
        type: "success",
        text: "Password reset email sent"
      });
    },

    async updateMemberRole(item) {
      const { success } = await API.updateOrgMemberRoles(
        this.org.id,
        item.member_id,
        item.role
      );
      if (!success) return;

      this.$notify({
        type: "success",
        text: `${item.name || item.invite_email} role changed to ${item.role}`
      });
    }
  }
};
</script>
